import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { getWhatsAppTemplates, getWhatsAppTemplate, getApiErrorMessage, apiClient } from '../../api-wa';
import '../../styles-wa/template-modal.css';

const REPLYABLE_BUTTON_TYPES = new Set(['QUICK_REPLY', 'FLOW']);
const getTemplateReplyButtons = (tpl) => {
    if (!tpl || !Array.isArray(tpl.buttons)) return [];
    return tpl.buttons
        .filter(b => {
            if (typeof b === 'string') return true;
            const btnType = String(b.type || '').toUpperCase();
            return !btnType || REPLYABLE_BUTTON_TYPES.has(btnType);
        })
        .map(b => (typeof b === 'string' ? b : b.text))
        .filter(Boolean);
};

// Simplified ButtonActionsEditor for Template Modal
const ButtonActionsEditor = ({ buttons, actions, onChange, templates, followUpWorkflows, depth = 0 }) => {
    const currentActions = actions || {};

    const updateBtn = (btnName, field, value) => {
        const next = { ...currentActions };
        const cfg = next[btnName] || { action: 'CONTINUE', reply_text: '' };
        next[btnName] = { ...cfg, [field]: value };
        onChange(next);
    };

    const updateBtnVar = (btnName, varIdx, value) => {
        const next = { ...currentActions };
        const cfg = next[btnName] || { action: 'SEND_TEMPLATE', template_variables: { body_variables: [] } };
        const tplVars = { ...(cfg.template_variables || { body_variables: [] }) };
        const bodyVars = [...(tplVars.body_variables || [])];
        bodyVars[varIdx] = value;
        tplVars.body_variables = bodyVars;
        next[btnName] = { ...cfg, template_variables: tplVars };
        onChange(next);
    };

    const removeBtn = (btnName) => {
        const next = { ...currentActions };
        delete next[btnName];
        onChange(next);
    };

    const allBtnNames = Array.from(new Set([...(buttons || []), ...Object.keys(currentActions)]));

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {allBtnNames.length > 0 && allBtnNames.map(btnName => {
                const actionCfg = currentActions[btnName] || { action: 'CONTINUE', reply_text: '' };
                const triggeredTpl = actionCfg.action === 'SEND_TEMPLATE'
                    ? templates.find(t => String(t.id) === String(actionCfg.template_id))
                    : null;

                let tplVarCount = 0;
                if (triggeredTpl) {
                    if (triggeredTpl.variables_count) tplVarCount = triggeredTpl.variables_count;
                    else if (triggeredTpl.body) {
                        const m = triggeredTpl.body.match(/\{\{([^}]+)\}\}/g);
                        if (m) tplVarCount = m.length;
                    }
                }

                const nestedButtons = triggeredTpl ? getTemplateReplyButtons(triggeredTpl) : [];
                const hasNestedConfig = Object.keys(actionCfg.next_button_actions || {}).length > 0;

                return (
                    <div key={btnName} style={{ background: '#f8fafc', borderRadius: '8px', padding: '10px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '11px', fontWeight: '700', color: 'var(--brand-purple)' }}>Button: "{btnName}"</span>
                            <button
                                type="button"
                                onClick={() => removeBtn(btnName)}
                                style={{ background: 'transparent', border: 'none', color: '#f87171', fontSize: '10px', cursor: 'pointer', fontWeight: '600' }}
                            >
                                Reset Action
                            </button>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '10px', alignItems: 'center' }}>
                            <label style={{ fontSize: '11px', fontWeight: '600', color: '#64748b' }}>Action On Click</label>
                            <select
                                value={actionCfg.action || 'CONTINUE'}
                                onChange={e => updateBtn(btnName, 'action', e.target.value)}
                                style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '11px', outline: 'none', background: '#fff' }}
                            >
                                <option value="CONTINUE">🟢 Continue / No specific action</option>
                                <option value="STOP">🔴 Stop / Divert Reminders for Contact</option>
                                <option value="SEND_TEMPLATE">📩 Send Template Message</option>
                                <option value="TRIGGER_FOLLOWUP">🔁 Trigger Follow-up Workflow</option>
                                <option value="FOLLOWUP_REPLY">💬 Send Custom Text Reply</option>
                            </select>
                        </div>

                        {actionCfg.action === 'SEND_TEMPLATE' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px', background: '#f1f5f9', padding: '8px', borderRadius: '6px' }}>
                                <label style={{ fontSize: '10px', fontWeight: '600', color: '#64748b' }}>Select Template</label>
                                <select
                                    value={actionCfg.template_id || ''}
                                    onChange={e => updateBtn(btnName, 'template_id', e.target.value)}
                                    style={{ padding: '6px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '11px', outline: 'none', background: '#fff' }}
                                >
                                    <option value="">-- Select Template --</option>
                                    {templates.map(t => (
                                        <option key={t.id} value={t.id}>{t.name}</option>
                                    ))}
                                </select>

                                {triggeredTpl && (
                                    <div style={{ background: '#fff', padding: '6px 8px', borderRadius: '4px', border: '1px solid #e2e8f0', fontSize: '10px', color: '#475569' }}>
                                        <strong>Preview:</strong> "{triggeredTpl.body}"
                                    </div>
                                )}

                                {tplVarCount > 0 && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '4px' }}>
                                        <label style={{ fontSize: '10px', fontWeight: '700', color: 'var(--brand-purple)' }}>
                                            Template Variables ({tplVarCount})
                                        </label>
                                        {Array.from({ length: tplVarCount }).map((_, vIdx) => {
                                            const currentVarVals = (actionCfg.template_variables || {}).body_variables || [];
                                            return (
                                                <div key={vIdx} style={{ display: 'flex', flexDirection: 'column', gap: '4px', background: '#fff', padding: '6px', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <span style={{ fontSize: '10px', fontWeight: '700', color: '#475569' }}>Variable &#123;&#123;{vIdx + 1}&#125;&#125;</span>
                                                        <button type="button" onClick={() => updateBtnVar(btnName, vIdx, '{{contact.name}}')} style={{ fontSize: '9px', background: 'rgba(124, 77, 255, 0.1)', color: 'var(--brand-purple)', border: 'none', borderRadius: '4px', padding: '1px 4px', cursor: 'pointer', fontWeight: '600' }}>+ Name</button>
                                                    </div>
                                                    <input
                                                        type="text"
                                                        placeholder={`Value for {{${vIdx + 1}}}`}
                                                        value={currentVarVals[vIdx] || ''}
                                                        onChange={e => updateBtnVar(btnName, vIdx, e.target.value)}
                                                        style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '11px', outline: 'none' }}
                                                    />
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {triggeredTpl && (nestedButtons.length > 0 || hasNestedConfig) && (
                                    <div style={{ marginTop: '6px', paddingLeft: '8px', borderLeft: '2px solid var(--brand-purple)', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <div style={{ fontSize: '10px', fontWeight: '700', color: 'var(--brand-purple)' }}>
                                            ↳ When "{triggeredTpl.name}" is clicked:
                                        </div>
                                        <ButtonActionsEditor
                                            buttons={nestedButtons}
                                            actions={actionCfg.next_button_actions || {}}
                                            onChange={(nextActions) => updateBtn(btnName, 'next_button_actions', nextActions)}
                                            templates={templates}
                                            followUpWorkflows={followUpWorkflows}
                                            depth={depth + 1}
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        {actionCfg.action === 'TRIGGER_FOLLOWUP' && (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '10px', alignItems: 'center', marginTop: '4px' }}>
                                <label style={{ fontSize: '10px', fontWeight: '600', color: '#64748b' }}>Select Workflow</label>
                                <select
                                    value={actionCfg.followup_workflow_id || ''}
                                    onChange={e => updateBtn(btnName, 'followup_workflow_id', e.target.value)}
                                    style={{ padding: '6px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '11px', outline: 'none', background: '#fff' }}
                                >
                                    <option value="">-- Select Workflow --</option>
                                    {followUpWorkflows.map(wf => (
                                        <option key={wf.id} value={wf.id}>{wf.name}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {actionCfg.action === 'FOLLOWUP_REPLY' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px' }}>
                                <label style={{ fontSize: '10px', fontWeight: '600', color: '#64748b' }}>Reply Text</label>
                                <textarea
                                    rows="2"
                                    placeholder="Enter text reply message to send..."
                                    value={actionCfg.reply_text || ''}
                                    onChange={e => updateBtn(btnName, 'reply_text', e.target.value)}
                                    style={{ padding: '6px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '11px', outline: 'none', resize: 'vertical' }}
                                />
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

const TemplateModal = ({ isOpen, onClose, onSelectTemplate, activeContact }) => {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [variableValues, setVariableValues] = useState({});
    const [headerMediaUrl, setHeaderMediaUrl] = useState('');
    const [headerMediaFile, setHeaderMediaFile] = useState(null);
    const [mediaUrlInput, setMediaUrlInput] = useState('');
    const [showMobilePreview, setShowMobilePreview] = useState(false);
    const [buttonActions, setButtonActions] = useState({});
    const [followUpWorkflows, setFollowUpWorkflows] = useState([]);
    const fileInputRef = useRef(null);

    const categories = ['All', 'MARKETING', 'UTILITY', 'AUTHENTICATION'];

    useEffect(() => {
        const fetchFullDetails = async () => {
            if (selectedTemplate) {
                try {
                    // Get full template details if not already present (buttons etc)
                    const fullTemplate = await getWhatsAppTemplate(selectedTemplate.id);
                    if (fullTemplate) {
                        const bodyText = fullTemplate.body || '';
                        const headerText = fullTemplate.header_text || '';
                        const variables = [
                            ...extractVariables(headerText),
                            ...extractVariables(bodyText)
                        ];
                        const autoFilled = getAutoFilledVariables();
                        const initVars = variables.reduce((acc, v) => {
                            acc[v] = autoFilled[v] || '';
                            return acc;
                        }, {});
                        
                        const autoMediaUrl = 
                            fullTemplate.uploaded_preview_image ||
                            fullTemplate.meta_media_url ||
                            fullTemplate.header_media ||
                            (fullTemplate.sample_values && (fullTemplate.sample_values.header_handle || fullTemplate.sample_values.media_url)) ||
                            '';
                        setVariableValues(initVars);
                        setHeaderMediaUrl(autoMediaUrl);
                        setHeaderMediaFile(null);
                        setMediaUrlInput(autoMediaUrl);
                        setButtonActions({}); // Reset button actions for new template
                        // Update selectedTemplate with full details
                        setSelectedTemplate(fullTemplate);
                    }
                } catch (err) {
                    console.error("Error fetching full template details:", err);
                }
            }
        };
        fetchFullDetails();
    }, [selectedTemplate?.id]);

    useEffect(() => {
        if (isOpen) {
            loadTemplates();
            loadFollowUpWorkflows();
        }
    }, [isOpen]);

    const loadFollowUpWorkflows = async () => {
        try {
            const response = await apiClient.get('/follow-ups/');
            setFollowUpWorkflows(response.data?.results ?? response.data ?? []);
        } catch (err) {
            console.error('Error fetching follow-up workflows:', err);
        }
    };

    const loadTemplates = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await getWhatsAppTemplates();
            // Ensure data is an array
            const templatesArray = Array.isArray(data) ? data : (data?.results || []);
            // Filter for approved or pending templates so they are visible during testing
            const approvedTemplates = templatesArray.filter(t => t.status === 'APPROVED' || t.status === 'PENDING' || t.status === 'PENDING_REVIEW');
            setTemplates(approvedTemplates);
            if (approvedTemplates.length > 0) {
                setSelectedTemplate(approvedTemplates[0]);
            }
        } catch (err) {
            const errorMsg = getApiErrorMessage(err, 'Failed to load templates');
            setError(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const filteredTemplates = templates.filter(template => {
        const matchesCategory = selectedCategory === 'All' || template.category === selectedCategory;
        const name = template.name || '';
        const body = template.body || '';
        const matchesSearch = 
            name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            body.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesCategory && matchesSearch;
    });

    // Extract variables from template body (e.g., {{variable_name}})
    const extractVariables = (text) => {
        const regex = /\{\{(\w+)\}\}/g;
        const variables = [];
        let match;
        while ((match = regex.exec(text)) !== null) {
            variables.push(match[1]);
        }
        return [...new Set(variables)]; // Remove duplicates
    };

    // Replace variables in template
    const replaceVariables = (text, values) => {
        let result = text;
        Object.keys(values).forEach(key => {
            const val = values[key];
            const placeholder = `{{${key}}}`;
            const replacement = val && val.trim() ? val : placeholder;
            result = result.split(placeholder).join(replacement);
        });
        return result;
    };

    // WhatsApp text formatting: *bold*, _italic_, ~strikethrough~
    const formatWhatsAppText = (text) => {
        if (!text) return '';
        // Split into parts, preserving formatting markers
        const parts = [];
        let remaining = text;
        const regex = /(\*[^\s*\n](?:[^*]*?[^\s*\n])?\*|_[^\s_\n](?:[^_]*?[^\s_\n])?_|~[^\s~\n](?:[^~]*?[^\s~\n])?~)/g;
        let lastIndex = 0;
        let match;
        while ((match = regex.exec(remaining)) !== null) {
            if (match.index > lastIndex) {
                parts.push(remaining.substring(lastIndex, match.index));
            }
            const raw = match[0];
            if (raw.startsWith('*') && raw.endsWith('*')) {
                parts.push(<strong key={match.index}>{raw.slice(1, -1)}</strong>);
            } else if (raw.startsWith('_') && raw.endsWith('_')) {
                parts.push(<em key={match.index}>{raw.slice(1, -1)}</em>);
            } else if (raw.startsWith('~') && raw.endsWith('~')) {
                parts.push(<s key={match.index}>{raw.slice(1, -1)}</s>);
            }
            lastIndex = match.index + raw.length;
        }
        if (lastIndex < remaining.length) {
            parts.push(remaining.substring(lastIndex));
        }
        return parts.length > 0 ? parts : text;
    };

    // Auto-fill variables from contact data
    const getAutoFilledVariables = () => {
        const filled = {};
        if (!activeContact) return filled;

        // Map common variable names to contact fields
        const mapping = {
            'name': activeContact.name,
            'customer_name': activeContact.name,
            'contact_name': activeContact.name,
            'phone': activeContact.phone_number,
            'phone_number': activeContact.phone_number,
            'email': activeContact.email,
            'company': activeContact.company,

        };

        return mapping;
    };

    const handleSubmit = async () => {
        if (!selectedTemplate) return;

        try {
            setSubmitting(true);
            
            const processedBody = replaceVariables(selectedTemplate.body || '', variableValues);

            onSelectTemplate({
                ...selectedTemplate,
                processedBody,
                variableValues,
                buttonActions,
                headerMediaUrl: headerMediaUrl || undefined,
                headerMediaFile: headerMediaFile || undefined,  // Actual File object, not Base64
            });
            onClose();
        } catch (err) {
            const errorMsg = getApiErrorMessage(err, 'Failed to prepare template');
            setError(errorMsg);
        } finally {
            setSubmitting(false);
        }
    };

    // Handle file selection - store actual File object
    const handleFileInputChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            // Create preview URL for display only (not for sending)
            const previewUrl = URL.createObjectURL(file);
            setHeaderMediaUrl(previewUrl);  // For preview in UI
            setHeaderMediaFile(file);  // Store actual File object for sending
            setMediaUrlInput('');  // Clear text input
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = '';  // Reset input
        }
    };

    if (!isOpen) {
        if (showMobilePreview) setShowMobilePreview(false); // Reset on close
        return null;
    }

    // Validation logic for "Send Template" button
    const variables = Object.keys(variableValues);
    const hasHeaderMedia = selectedTemplate && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(selectedTemplate.header_type);

    const allVariablesFilled = variables.length === 0 || variables.every(v => variableValues[v] && variableValues[v].trim() !== '');
    const headerMediaFilled = !hasHeaderMedia || (headerMediaUrl && headerMediaUrl.trim() !== '');
    const canSend = allVariablesFilled && headerMediaFilled;

    const mediaTypeLabel = selectedTemplate?.header_type === 'IMAGE' ? 'Image'
        : selectedTemplate?.header_type === 'VIDEO' ? 'Video'
        : 'Document';
    const acceptAttr = selectedTemplate?.header_type === 'IMAGE' ? 'image/*'
        : selectedTemplate?.header_type === 'VIDEO' ? 'video/*'
        : '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip';

  return createPortal(
    <div className="wa-module">
      <div className="template-modal-overlay" onClick={onClose}></div>

      <div className="template-modal">
        <div className="template-modal-header">
          <h2>Message Templates</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

                {/* Content */}
                <div className="template-modal-content">
                    {/* Left Sidebar - Categories & Search */}
                    <div className="template-sidebar">
                        <div className="search-box">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="11" cy="11" r="8"></circle>
                                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                            </svg>
                            <input
                                type="text"
                                placeholder="Search templates..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <div className="category-list">
                            <h3>Categories</h3>
                            {categories.map(category => (
                                <button
                                    key={category}
                                    className={`category-item ${selectedCategory === category ? 'active' : ''}`}
                                    onClick={() => setSelectedCategory(category)}
                                >
                                    {category === 'All' ? 'All Templates' : category}
                                    <span className="count">
                                        {category === 'All' 
                                            ? templates.length 
                                            : templates.filter(t => t.category === category).length}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Right Content - Template List & Preview */}
                    <div className="template-main">
                        {/* Template List */}
                        <div className="template-list">
                            <h3>Templates</h3>
                            {loading ? (
                                <div className="loading">Loading templates...</div>
                            ) : error ? (
                                <div className="error">{error}</div>
                            ) : filteredTemplates.length === 0 ? (
                                <div className="no-templates">No templates found</div>
                            ) : (
                                <div className="templates-scroll">
                                    {filteredTemplates.map(template => (
                                        <div
                                            key={template.id}
                                            className={`template-card ${selectedTemplate?.id === template.id ? 'active' : ''}`}
                                            onClick={() => {
                                                const autoMediaUrl = 
                                                    template.uploaded_preview_image ||
                                                    template.meta_media_url ||
                                                    template.header_media ||
                                                    (template.sample_values && (template.sample_values.header_handle || template.sample_values.media_url)) ||
                                                    '';
                                                setSelectedTemplate(template);
                                                setHeaderMediaUrl(autoMediaUrl);
                                                setMediaUrlInput(autoMediaUrl);
                                                setHeaderMediaFile(null);
                                                setShowMobilePreview(true);
                                            }}
                                        >
                                            <div className="template-card-header">
                                                <h4>{template.name}</h4>
                                                <span className={`category-badge ${template.category.toLowerCase()}`}>
                                                    {template.category}
                                                </span>
                                            </div>
                                            <p className="template-preview">
                                                {(template.body || '').substring(0, 60)}...
                                            </p>
                                            <div className="template-meta">
                                                <small>{new Date(template.created_at).toLocaleDateString()}</small>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Preview */}
                        <div className={`template-preview ${showMobilePreview ? 'mobile-preview-active' : ''}`}>
                            {selectedTemplate ? (
                                <>
                                    <div className="preview-header">
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <button className="mobile-back-btn" onClick={() => setShowMobilePreview(false)}>
                                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>
                                            </button>
                                            <h3>Preview</h3>
                                        </div>
                                        <span className={`category-badge ${selectedTemplate.category.toLowerCase()}`}>
                                            {selectedTemplate.category}
                                        </span>
                                    </div>
                                    <div className="preview-content">
                                        <div className="preview-box">
                                            <p className="template-name">{selectedTemplate.name}</p>
                                            
                                            {/* Media Area */}
                                            {hasHeaderMedia && (
                                                <div className="compact-media-preview" style={{ marginBottom: '16px' }}>
                                                    {headerMediaUrl ? (
                                                        <div className="compact-media-preview-container">
                                                            {selectedTemplate.header_type === 'IMAGE' && (
                                                                (headerMediaUrl.startsWith('http') || headerMediaUrl.startsWith('data:') || headerMediaUrl.startsWith('blob:')) ? (
                                                                    <img src={headerMediaUrl} alt="Preview" />
                                                                ) : (
                                                                    <div className="meta-handle-display-box" style={{ padding: '24px', background: 'linear-gradient(135deg, rgba(117, 81, 255, 0.05) 0%, rgba(99, 102, 241, 0.1) 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', borderRadius: '12px', border: '1px solid rgba(117, 81, 255, 0.2)', width: '100%', boxSizing: 'border-box' }}>
                                                                        <span style={{ fontSize: '32px' }}>🖼️</span>
                                                                        <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--brand-purple)' }}>Meta Sync Media Configured</span>
                                                                        <span style={{ fontSize: '11px', color: '#64748b', fontFamily: 'monospace' }}>ID: {headerMediaUrl}</span>
                                                                    </div>
                                                                )
                                                            )}
                                                            {selectedTemplate.header_type === 'VIDEO' && (
                                                                (headerMediaUrl.startsWith('http') || headerMediaUrl.startsWith('data:') || headerMediaUrl.startsWith('blob:')) ? (
                                                                    <video src={headerMediaUrl} controls />
                                                                ) : (
                                                                    <div className="meta-handle-display-box" style={{ padding: '24px', background: 'linear-gradient(135deg, rgba(117, 81, 255, 0.05) 0%, rgba(99, 102, 241, 0.1) 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', borderRadius: '12px', border: '1px solid rgba(117, 81, 255, 0.2)', width: '100%', boxSizing: 'border-box' }}>
                                                                        <span style={{ fontSize: '32px' }}>🎥</span>
                                                                        <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--brand-purple)' }}>Meta Sync Video Configured</span>
                                                                        <span style={{ fontSize: '11px', color: '#64748b', fontFamily: 'monospace' }}>ID: {headerMediaUrl}</span>
                                                                    </div>
                                                                )
                                                            )}
                                                            {selectedTemplate.header_type === 'DOCUMENT' && (
                                                                <div style={{ padding: '24px', background: '#f8fafc', display: 'flex', alignItems: 'center', gap: '12px', border: '1px solid var(--border-color)', borderRadius: '12px', width: '100%', boxSizing: 'border-box' }}>
                                                                    <span style={{ fontSize: '32px' }}>📄</span>
                                                                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#334155', wordBreak: 'break-all' }}>
                                                                        {headerMediaFile?.name || (headerMediaUrl.startsWith('http') ? 'Document Attached' : `Meta Sync Document: ${headerMediaUrl}`)}
                                                                    </span>
                                                                </div>
                                                            )}
                                                            <button 
                                                                onClick={() => { setHeaderMediaUrl(''); setHeaderMediaFile(null); setMediaUrlInput(''); }}
                                                                className="remove-media-btn"
                                                            >✕</button>
                                                        </div>
                                                    ) : (
                                                        <div className="dashed-media-placeholder">
                                                            <span>Header {mediaTypeLabel} Required</span>
                                                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '12px', flexWrap: 'wrap', justifyContent: 'center', flexDirection: 'column' }}>
                                                                {/* File Upload Button */}
                                                                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                                                    <input
                                                                        ref={fileInputRef}
                                                                        type="file"
                                                                        accept={acceptAttr}
                                                                        onChange={handleFileInputChange}
                                                                        style={{ display: 'none' }}
                                                                    />
                                                                    <button
                                                                        onClick={() => fileInputRef.current?.click()}
                                                                        style={{
                                                                            background: 'var(--brand-purple)',
                                                                            color: 'white',
                                                                            border: 'none',
                                                                            borderRadius: '6px',
                                                                            padding: '6px 12px',
                                                                            fontSize: '12px',
                                                                            cursor: 'pointer',
                                                                            fontWeight: 600,
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: '4px'
                                                                        }}
                                                                    >
                                                                        📁 Upload {mediaTypeLabel}
                                                                    </button>
                                                                </div>

                                                                {/* OR Divider */}
                                                                <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600 }}>—” or —”</span>

                                                                {/* URL Input */}
                                                                <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600 }}>Paste media URL:</span>
                                                                <div style={{ display: 'flex', gap: '4px' }}>
                                                                    <input 
                                                                        type="text"
                                                                        placeholder={`Paste ${mediaTypeLabel} URL`}
                                                                        value={mediaUrlInput}
                                                                        style={{ 
                                                                            padding: '6px 12px', 
                                                                            borderRadius: '6px', 
                                                                            border: '1px solid #cbd5e1', 
                                                                            fontSize: '12px',
                                                                            outline: 'none',
                                                                            width: '200px'
                                                                        }}
                                                                        onChange={(e) => setMediaUrlInput(e.target.value)}
                                                                        onKeyDown={(e) => {
                                                                            if (e.key === 'Enter' && mediaUrlInput) {
                                                                                setHeaderMediaUrl(mediaUrlInput);
                                                                                setHeaderMediaFile(null);
                                                                            }
                                                                        }}
                                                                    />
                                                                    <button
                                                                        onClick={() => {
                                                                            if (mediaUrlInput) {
                                                                                setHeaderMediaUrl(mediaUrlInput);
                                                                                setHeaderMediaFile(null);
                                                                            }
                                                                        }}
                                                                        style={{
                                                                            background: 'var(--brand-purple)',
                                                                            color: 'white',
                                                                            border: 'none',
                                                                            borderRadius: '6px',
                                                                            padding: '0 12px',
                                                                            fontSize: '12px',
                                                                            cursor: 'pointer',
                                                                            fontWeight: 600
                                                                        }}
                                                                    >
                                                                        Apply
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            <div className="template-body message-body">
                                                {formatWhatsAppText(replaceVariables(selectedTemplate.body || '', variableValues))}
                                            </div>

                                            {selectedTemplate.footer_text && (
                                                <div className="template-footer">
                                                    {selectedTemplate.footer_text}
                                                </div>
                                            )}

                                            {/* Buttons Preview */}
                                            {selectedTemplate.buttons && selectedTemplate.buttons.length > 0 && (
                                                <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid var(--border-color)' }}>
                                                    {selectedTemplate.buttons.map((btn, idx) => (
                                                        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', marginTop: '4px', background: 'var(--surface-soft-2)', borderRadius: '8px', fontSize: '12px', fontWeight: '600', color: 'var(--brand-purple)' }}>
                                                            {btn.type === 'URL' && '🔗'}
                                                            {btn.type === 'QUICK_REPLY' && '↩️'}
                                                            {btn.type === 'PHONE_NUMBER' && '📞'}
                                                            {btn.text}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        {/* Inputs Section */}
                                        <div className="inputs-section" style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '8px' }}>
                                            {/* Variable Inputs */}
                                            {variables.length > 0 && (
                                                <div className="variables-info">
                                                    <h4>Personalize Variables</h4>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                        {variables.sort((a,b) => a-b).map(v => (
                                                            <div key={v} className="field-card-premium" style={{ margin: 0, padding: '6px 10px' }}>
                                                                <div className="var-pill-purple-premium" style={{ padding: '4px 8px', fontSize: '11px' }}>{`{{${v}}}`}</div>
                                                                <input
                                                                    type="text"
                                                                    className="premium-input-transparent"
                                                                    style={{ fontSize: '13px' }}
                                                                    placeholder={`Value for ${v}...`}
                                                                    value={variableValues[v] || ''}
                                                                    onChange={(e) => setVariableValues(prev => ({ ...prev, [v]: e.target.value }))}
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {selectedTemplate && getTemplateReplyButtons(selectedTemplate).length > 0 && (
                                                <div className="button-actions-info" style={{ marginTop: '8px' }}>
                                                     <h4 style={{ fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '8px' }}>Button Reply Follow-up Actions</h4>
                                                     <ButtonActionsEditor
                                                         buttons={getTemplateReplyButtons(selectedTemplate)}
                                                         actions={buttonActions}
                                                         onChange={setButtonActions}
                                                         templates={templates}
                                                         followUpWorkflows={followUpWorkflows}
                                                     />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    
                                    <div className="preview-actions" style={{ marginTop: 'auto', paddingTop: '16px', borderTop: '1px solid var(--border-color)', position: 'sticky', bottom: 0 }}>
                                        <button 
                                            className={`use-template-btn ${!canSend ? 'disabled' : ''}`}
                                            onClick={handleSubmit}
                                            disabled={submitting || !canSend}
                                            style={{ 
                                                background: canSend ? 'linear-gradient(135deg, var(--brand-purple) 0%, #4f46e5 100%)' : '#cbd5e1',
                                                cursor: canSend ? 'pointer' : 'not-allowed',
                                                margin: 0
                                            }}
                                        >
                                            {submitting ? 'Sending...' : 'Send Template'}
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <div className="no-selection">
                                    Select a template to preview
                                </div>
                            )}
                        </div>
                    </div>
                </div>
      </div>
    </div>,
    document.body
  );
};

export default TemplateModal;






